import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, Link, useLocation, useSearchParams } from 'react-router-dom';
import { Gallery } from './pages/Gallery';
import { LayoutDetailPage } from './pages/LayoutDetail';
import { DeckPage } from './pages/DeckPage';
import { MyDecksPage } from './pages/MyDecksPage';
import { MyDeckDetailPage } from './pages/MyDeckDetailPage';
import { SharedDeckPage } from './pages/SharedDeckPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { FamiliesPage } from './pages/admin/FamiliesPage';
import { CatalogDocumentsPage } from './pages/admin/CatalogDocumentsPage';
import { LayoutsPage } from './pages/admin/LayoutsPage';
import { LayoutAdminPage } from './pages/admin/LayoutAdminPage';
import { RendersTab } from './pages/admin/RendersPage';
import { BomTab } from './pages/admin/BomPage';
import { SourceFilesTab } from './pages/admin/SourceFilesTab';
import { ProductsPage } from './pages/admin/ProductsPage';
import { SnapshotsPage } from './pages/admin/SnapshotsPage';
import { UsersPage } from './pages/admin/UsersPage';
import { SharesPage } from './pages/admin/SharesPage';
import { ApiKeysPage } from './pages/admin/ApiKeysPage';
import { useDeck } from './lib/deckContext';
import { AuthGate } from './components/AuthGate';
import { RoleGate } from './components/RoleGate';
import { AUTH_BYPASSED, getActiveUser, signOut } from './lib/auth/msal';
import { fetchFamilies, fetchLayouts, fetchProducts, type Family, type ProductItem } from './lib/api';
import { useRole } from './lib/roleContext';

export default function App() {
  const location = useLocation();
  const isPublicShare = location.pathname.startsWith('/shared/');

  // The /shared/:token route is customer-facing and brings its own chrome.
  // It deliberately skips the internal Vari header AND the AuthGate so a
  // customer can view a deck without a Vari Entra account.
  if (isPublicShare) {
    return (
      <Routes>
        <Route path="/shared/:token" element={<SharedDeckPage />} />
      </Routes>
    );
  }

  return (
    <AuthGate>
      <InternalShell />
    </AuthGate>
  );
}

function InternalShell() {
  const location = useLocation();
  const isGallery = location.pathname === '/';
  const { count } = useDeck();
  // In auth-bypass mode (local dev) there's no MSAL account, so fall back to a
  // stand-in identity — otherwise the profile menu can't be previewed locally.
  const user = AUTH_BYPASSED ? { name: 'Dev User', email: 'auth bypassed' } : getActiveUser();
  const { role } = useRole();

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <header className="shrink-0 bg-vari-navy text-white border-b border-vari-navy/20">
        <div className="max-w-full mx-auto px-6 py-3 flex items-center gap-6">
          <Link to="/" className="shrink-0 flex items-center gap-1.5 text-lg font-semibold tracking-tight text-white">
            <span>Vari</span>
            <BulbMark className="h-6 w-6" />
            <span>Idea Starter</span>
          </Link>

          {/* Left — gallery filters. Only on the gallery route, where they
              actually drive the flipbook: search a product, or filter by space. */}
          {isGallery && (
            <div className="flex items-center gap-2">
              <ProductSearchPill />
              <SpaceFilterPill />
            </div>
          )}

          <div className="ml-auto flex items-center gap-3 shrink-0">
            <Link
              to="/deck"
              className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-1.5 text-sm font-medium transition-colors text-white"
            >
              <span>Deck Builder</span>
              <span
                className={`min-w-6 text-center rounded-full px-2 text-xs font-semibold ${
                  count > 0 ? 'bg-vari-yellow text-vari-navy' : 'bg-white/15 text-white/70'
                }`}
              >
                {count}
              </span>
            </Link>
            {user ? (
              <ProfileMenu user={user} role={role} />
            ) : (
              <span className="text-xs text-white/60 hidden sm:inline">
                {AUTH_BYPASSED ? 'Internal · v0.1 · auth bypass' : 'Internal · v0.1'}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-hidden">
        <Routes>
          <Route path="/" element={<Gallery />} />
          <Route path="/layouts/:code" element={<LayoutDetailPage />} />
          <Route path="/deck" element={<DeckPage />} />
          <Route path="/my-decks" element={<MyDecksPage />} />
          <Route path="/my-decks/:id" element={<MyDeckDetailPage />} />

          {/* Admin panel — design+ required at the outer gate, admin-only pages
              have an inner RoleGate to show AccessDenied for design users */}
          <Route
            path="/admin/*"
            element={
              <RoleGate require="design">
                <AdminLayout />
              </RoleGate>
            }
          >
            <Route index element={<Navigate to="/admin/layouts" replace />} />
            <Route path="layouts" element={<LayoutsPage />} />
            <Route path="layouts/:id" element={<LayoutAdminPage />}>
              <Route index element={<Navigate to="renders" replace />} />
              <Route path="renders" element={<RendersTab />} />
              <Route path="bom" element={<BomTab />} />
              <Route path="files" element={<SourceFilesTab />} />
            </Route>
            <Route path="families" element={<FamiliesPage />} />
            <Route path="catalog-documents" element={<CatalogDocumentsPage />} />
            <Route
              path="products"
              element={
                <RoleGate require="admin">
                  <ProductsPage />
                </RoleGate>
              }
            />
            <Route
              path="snapshots"
              element={
                <RoleGate require="admin">
                  <SnapshotsPage />
                </RoleGate>
              }
            />
            <Route
              path="users"
              element={
                <RoleGate require="admin">
                  <UsersPage />
                </RoleGate>
              }
            />
            <Route
              path="shares"
              element={
                <RoleGate require="admin">
                  <SharesPage />
                </RoleGate>
              }
            />
            <Route
              path="api-keys"
              element={
                <RoleGate require="admin">
                  <ApiKeysPage />
                </RoleGate>
              }
            />
          </Route>
        </Routes>
      </main>

      {!isGallery && !location.pathname.startsWith('/admin') && (
        <footer className="shrink-0 border-t border-vari-soft-gray bg-white">
          <div className="max-w-7xl mx-auto px-6 py-3 text-xs text-vari-gray">
            Prices effective 01/2026. For internal Vari use only.
          </div>
        </footer>
      )}
    </div>
  );
}

/** The Idea Starter lightbulb, sans tile — for inline use in the brand mark.
 * Mirrors public/favicon.svg (rays + bulb + base); colors are fixed to the
 * brand palette so it reads on the navy header. */
function BulbMark({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="4 2 24 24" className={className} aria-hidden="true">
      <g stroke="#f6d44d" strokeWidth="1.7" strokeLinecap="round">
        <path d="M16 6.2V3.2" />
        <path d="M20.7 7.8l1.8-2.4" />
        <path d="M11.3 7.8L9.5 5.4" />
        <path d="M23.4 11.5l2.9-1" />
        <path d="M8.6 11.5l-2.9-1" />
      </g>
      <circle cx="16" cy="14" r="5.6" fill="#f6d44d" />
      <rect x="12.9" y="18.6" width="6.2" height="6.2" rx="1.5" fill="#f6d44d" />
      <path d="M13.5 21h5M14 23h4" stroke="#003349" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/** Close `onClose` when a mousedown lands outside `ref`. Shared by the two
 * header filter popovers. */
function useOutsideClose(ref: React.RefObject<HTMLElement | null>, onClose: () => void): void {
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

/** Account pill in the top-right. Clicking the name opens a dropdown with the
 * signed-in identity and the Sign out action (previously a bare link). */
function ProfileMenu({
  user,
  role,
}: {
  user: { name?: string; email?: string };
  role: string | null;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClose(ref, () => setOpen(false));

  const canDesign = role === 'design' || role === 'admin';
  const isAdmin = role === 'admin';

  const displayName = user.name ?? user.email ?? 'Account';
  const initials =
    (user.name ?? user.email ?? '?')
      .split(/[\s@.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || '?';

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 pl-1 pr-3 py-1 text-sm font-medium text-white transition-colors"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-vari-yellow text-vari-navy text-xs font-semibold">
          {initials}
        </span>
        <span className="max-w-[10rem] truncate">{displayName}</span>
        <svg
          viewBox="0 0 10 6"
          className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-64 bg-white rounded-xl border border-vari-soft-gray shadow-xl overflow-hidden text-vari-navy">
          <div className="px-4 py-3 border-b border-vari-soft-gray">
            <p className="text-sm font-semibold truncate">{user.name ?? user.email}</p>
            {user.email && <p className="text-xs text-vari-gray truncate">{user.email}</p>}
            {role && role !== 'user' && (
              <span className="mt-1.5 inline-block px-1.5 py-0.5 bg-vari-yellow/40 text-vari-navy rounded text-[10px] font-semibold uppercase tracking-wide">
                {role}
              </span>
            )}
          </div>

          <nav className="py-1 border-b border-vari-soft-gray">
            {canDesign && (
              <MenuLink to="/admin" onNavigate={() => setOpen(false)}>
                <MenuIcon path="M4 5h16M4 12h16M4 19h10" />
                Design
              </MenuLink>
            )}
            {isAdmin && (
              <MenuLink to="/admin/users" onNavigate={() => setOpen(false)}>
                <MenuIcon path="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6l7-3z" />
                Admin
              </MenuLink>
            )}
            <MenuLink to="/my-decks" onNavigate={() => setOpen(false)}>
              <MenuIcon path="M4 7l8-3 8 3-8 3-8-3zM4 12l8 3 8-3M4 17l8 3 8-3" />
              My decks
            </MenuLink>
          </nav>

          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full text-left px-4 py-2.5 text-sm text-vari-navy hover:bg-vari-beige/50 flex items-center gap-2.5 transition-colors"
          >
            <svg viewBox="0 0 16 16" className="w-4 h-4 text-vari-gray" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 14H3.5A1.5 1.5 0 012 12.5v-9A1.5 1.5 0 013.5 2H6" />
              <path d="M10.5 11l3-3-3-3" />
              <path d="M13.5 8H6" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** A navigation row inside the profile dropdown. Closes the menu on click. */
function MenuLink({
  to,
  onNavigate,
  children,
}: {
  to: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="w-full text-left px-4 py-2.5 text-sm text-vari-navy hover:bg-vari-beige/50 flex items-center gap-2.5 transition-colors"
    >
      {children}
    </Link>
  );
}

function MenuIcon({ path }: { path: string }) {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 text-vari-gray shrink-0" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

/** The trigger pill for a header filter dropdown. Highlights when a filter is
 * active and rotates its caret while open. */
function FilterPillButton({
  open,
  active,
  onClick,
  children,
}: {
  open: boolean;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
        active ? 'bg-white/25 text-white' : 'bg-white/10 text-white/90 hover:bg-white/20'
      }`}
    >
      {children}
      <svg
        viewBox="0 0 10 6"
        className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M1 1l4 4 4-4" />
      </svg>
    </button>
  );
}

/** "Search by Product" pill → popover with a type-ahead product search. The
 * selection is held in the `?product=<id>` URL param so the gallery (a sibling
 * of the header) picks it up and filters. Replaces the old in-gallery search
 * bar. */
function ProductSearchPill() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useOutsideClose(ref, () => setOpen(false));

  useEffect(() => {
    fetchProducts()
      .then((r) => setProducts(r.products))
      .catch(() => {/* non-fatal — search just stays empty */});
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const productId = searchParams.get('product');
  const selected = productId ? products.find((p) => String(p.id) === productId) ?? null : null;

  const select = (p: ProductItem): void => {
    setSearchParams((sp) => { sp.set('product', String(p.id)); return sp; });
    setQuery('');
    setOpen(false);
  };
  const clear = (): void => {
    setSearchParams((sp) => { sp.delete('product'); return sp; });
    setQuery('');
  };

  const filtered = query.trim()
    ? products.filter(
        (p) =>
          p.name.toLowerCase().includes(query.toLowerCase()) ||
          p.sku.toLowerCase().includes(query.toLowerCase()),
      )
    : products;

  return (
    <div ref={ref} className="relative">
      <FilterPillButton open={open} active={!!selected} onClick={() => setOpen((o) => !o)}>
        {/* Roomier than the 12rem it used to be so a selected ESD's finish stays
            readable in the pill, not just in the open dropdown. */}
        <span className="max-w-[14rem] xl:max-w-[22rem] truncate" title={selected ? selected.name : undefined}>
          {selected ? selected.name : 'Search by Product'}
        </span>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear product filter"
            onClick={(e) => { e.stopPropagation(); clear(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clear(); } }}
            className="ml-0.5 -mr-1 flex h-4 w-4 items-center justify-center rounded-full hover:bg-white/20"
          >
            <svg viewBox="0 0 16 16" className="w-2.5 h-2.5" fill="currentColor">
              <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </span>
        )}
      </FilterPillButton>

      {/* w-[34rem]: wide enough that the longest catalog names render in full
          next to their SKU — e.g. "ESD 60X30 ComfortEdgeTM + Cable Tray
          (Light Wood)" — so reps can tell finishes apart before selecting. */}
      {open && (
        <div className="absolute left-0 top-full mt-2 z-50 w-[34rem] max-w-[calc(100vw-2rem)] bg-white rounded-xl border border-vari-soft-gray shadow-xl overflow-hidden text-vari-navy">
          <div className="p-2 border-b border-vari-soft-gray">
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder="Search products…"
              onChange={(e) => setQuery(e.target.value)}
              className="w-full px-3 py-2 text-sm bg-vari-beige/30 rounded-lg border border-vari-soft-gray/50 focus:outline-none focus:border-vari-navy transition-colors"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-vari-gray">No products found.</li>
            ) : (
              filtered.slice(0, 60).map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => select(p)}
                    className={`w-full text-left px-4 py-2 hover:bg-vari-beige/50 transition-colors flex items-baseline gap-3 ${
                      selected?.id === p.id ? 'bg-vari-beige/40' : ''
                    }`}
                  >
                    <span className="text-sm truncate">{p.name}</span>
                    <span className="text-[10px] text-vari-gray font-mono shrink-0 ml-auto">{p.sku}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {selected && (
            <div className="border-t border-vari-soft-gray px-3 py-2">
              <button type="button" onClick={clear} className="text-xs text-vari-gray hover:text-vari-navy">
                Clear product filter
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** "Filter by Space" pill → popover with a checkbox per family (multi-select).
 * Selection lives in the `?spaces=a,b` URL param. Each row also has a "+ deck"
 * action that bulk-adds that family's Alpha (environmental-1) images. */
function SpaceFilterPill() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [families, setFamilies] = useState<Family[] | null>(null);
  const [open, setOpen] = useState(false);
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const { deck, addLayouts, removeLayoutsByFamily } = useDeck();
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClose(ref, () => setOpen(false));

  useEffect(() => {
    let cancelled = false;
    fetchFamilies()
      .then((r) => { if (!cancelled) setFamilies(r.families); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  const selected = (searchParams.get('spaces') ?? '').split(',').filter(Boolean);
  const selectedSet = new Set(selected);

  const toggleSpace = (slug: string): void => {
    const next = new Set(selectedSet);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setSearchParams((p) => {
      const arr = [...next];
      if (arr.length) p.set('spaces', arr.join(','));
      else p.delete('spaces');
      return p;
    });
  };

  const clearAll = (): void => {
    setSearchParams((p) => { p.delete('spaces'); return p; });
  };

  // "+ deck": add just each layout's Alpha image; toggles off (removes) when the
  // whole family is already in the deck.
  const toggleFamilyInDeck = async (family: Family): Promise<void> => {
    const inDeck = deck.items.filter((i) => i.familySlug === family.slug).length;
    if (inDeck > 0) {
      removeLayoutsByFamily(family.slug);
      return;
    }
    setLoadingSlug(family.slug);
    try {
      const { layouts } = await fetchLayouts([family.slug]);
      const alpha = layouts.filter((l) => Boolean(l.renders['environmental-1']));
      addLayouts(alpha, ['environmental-1']);
    } finally {
      setLoadingSlug(null);
    }
  };

  return (
    <div ref={ref} className="relative">
      <FilterPillButton open={open} active={selected.length > 0} onClick={() => setOpen((o) => !o)}>
        Filter by Space
        {selected.length > 0 && (
          <span className="ml-0.5 min-w-5 text-center rounded-full bg-vari-yellow text-vari-navy px-1.5 text-xs font-semibold">
            {selected.length}
          </span>
        )}
      </FilterPillButton>

      {open && families && (
        <div className="absolute left-0 top-full mt-2 z-50 w-72 bg-white rounded-xl border border-vari-soft-gray shadow-xl overflow-hidden text-vari-navy">
          <ul className="max-h-80 overflow-y-auto py-1">
            {families.map((f) => {
              const checked = selectedSet.has(f.slug);
              const inDeck = deck.items.filter((i) => i.familySlug === f.slug).length;
              const allInDeck = inDeck > 0 && inDeck === f.layoutCount;
              const loading = loadingSlug === f.slug;
              return (
                <li key={f.slug} className="flex items-center gap-2 pl-3 pr-2 py-1.5 hover:bg-vari-beige/40">
                  <button
                    type="button"
                    onClick={() => toggleSpace(f.slug)}
                    className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                        checked ? 'bg-vari-navy border-vari-navy text-white' : 'border-vari-soft-gray text-transparent'
                      }`}
                    >
                      <svg viewBox="0 0 10 10" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1.5 5l2.5 2.5 4.5-4" />
                      </svg>
                    </span>
                    <span className="text-sm truncate">{f.name}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void toggleFamilyInDeck(f)}
                    disabled={loading}
                    title={allInDeck ? `Remove all ${f.name} from deck` : `Add all ${f.name} to deck`}
                    className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium border transition-colors disabled:opacity-50 ${
                      allInDeck
                        ? 'bg-vari-navy text-white border-vari-navy'
                        : 'border-vari-soft-gray text-vari-gray hover:border-vari-navy hover:text-vari-navy'
                    }`}
                  >
                    {loading ? '…' : allInDeck ? '✓ deck' : '+ deck'}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between border-t border-vari-soft-gray px-3 py-2 text-xs">
            <button
              type="button"
              onClick={clearAll}
              disabled={selected.length === 0}
              className="text-vari-gray hover:text-vari-navy disabled:opacity-40"
            >
              Clear all
            </button>
            <span className="text-vari-gray">
              {selected.length ? `${selected.length} selected` : 'All spaces'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
